import { supabase } from './supabase';

// WordPress site configuration from database
interface WordPressSiteConfig {
  wordpress_url: string;
  username: string;
  app_password: string;
  is_active: boolean;
}

// Get site label from localStorage (user preference) or env variable (default)
const getWordPressSiteLabel = () => {
  const storedLabel = localStorage.getItem('wp_site_label');
  return storedLabel || process.env.REACT_APP_WP_SITE_LABEL;
};

// Fetch WordPress site configuration from database
const getWordPressSiteConfig = async (specificSiteLabel?: string): Promise<WordPressSiteConfig | null> => {
  const siteLabel = specificSiteLabel || getWordPressSiteLabel();

  if (!siteLabel) {
    console.warn('No WordPress site label configured');
    return null;
  }

  try {
    const { data, error } = await supabase
      .from('wordpress_sites')
      .select('wordpress_url, username, app_password, is_active')
      .eq('label_name', siteLabel)
      .eq('is_active', true)
      .maybeSingle();

    if (error) {
      console.error('Error fetching WordPress site config:', error);
      return null;
    }

    if (!data) {
      console.warn(`No active WordPress site found for label: ${siteLabel}`);

      // Fallback to environment variables if available (only if no specific site was requested)
      if (!specificSiteLabel) {
        const fallbackUrl = process.env.REACT_APP_WORDPRESS_URL;
        const fallbackUsername = process.env.REACT_APP_WP_USERNAME;
        const fallbackPassword = process.env.REACT_APP_WP_APP_PASSWORD;

        if (fallbackUrl && fallbackUsername && fallbackPassword) {
          console.log('Using fallback credentials from environment variables');
          return {
            wordpress_url: fallbackUrl,
            username: fallbackUsername,
            app_password: fallbackPassword,
            is_active: true,
          };
        }
      }

      return null;
    }

    return data;
  } catch (err) {
    console.error('Failed to fetch WordPress site config:', err);
    return null;
  }
};

// Create auth headers with credentials from config
const getAuthHeaders = (username?: string, appPassword?: string): HeadersInit => {
  if (username && appPassword) {
    const credentials = btoa(`${username}:${appPassword}`);
    return {
      'Authorization': `Basic ${credentials}`,
    };
  }
  return {};
};

export interface WordPressPost {
  id: number;
  title: { rendered: string };
  content: { rendered: string };
  excerpt: { rendered: string };
  slug: string;
  status: string;
  author: number;
  date: string;
  modified: string;
  categories: number[];
  tags: number[];
  featured_media: number;
  _embedded?: {
    author?: Array<{
      id: number;
      name: string;
      slug: string;
      description: string;
      avatar_urls: { [key: string]: string };
    }>;
    'wp:featuredmedia'?: Array<{
      source_url: string;
    }>;
  };
}

export interface WordPressAuthor {
  id: number;
  name: string;
  slug: string;
  description: string;
  avatar_urls: { [key: string]: string };
}

export interface Page {
  id: number;
  title: { rendered: string };
  content: { rendered: string };
  slug: string;
}

export const wordpressService = {
  async getPosts(page: number = 1, perPage: number = 100, specificSiteLabel?: string): Promise<WordPressPost[]> {
    // Fetch WordPress configuration from database
    const config = await getWordPressSiteConfig(specificSiteLabel);

    if (!config) {
      throw new Error('WordPress site not configured. Please:\n1. Click Configure button in swimlane header\n2. Select a site and add WordPress URL\n3. Enter username and Application Password');
    }

    if (!config.wordpress_url) {
      throw new Error('WordPress URL not configured for this site');
    }

    if (!config.username || !config.app_password) {
      throw new Error('WordPress credentials not configured. Please set username and Application Password in the site configuration.');
    }

    // Use custom CPNode plugin endpoint that supports all post statuses
    // This endpoint requires the CPNode REST API Extension plugin to be installed and activated
    const url = `${config.wordpress_url}/wp-json/cpnode/v1/posts?page=${page}&per_page=${perPage}`;

    console.log('Fetching from WordPress:', url);
    console.log('Using credentials:', { username: config.username, passwordLength: config.app_password.length });

    const response = await fetch(url, {
      headers: {
        ...getAuthHeaders(config.username, config.app_password),
        'Content-Type': 'application/json',
      },
      credentials: 'omit', // Don't send cookies, use Basic Auth only
    });

    console.log('Response status:', response.status);
    console.log('Response headers:', Object.fromEntries(response.headers.entries()));

    const contentType = response.headers.get('content-type');

    if (!response.ok) {
      const errorText = await response.text();
      console.error('WordPress API Error:', { status: response.status, contentType, errorText: errorText.substring(0, 500) });

      if (response.status === 404) {
        throw new Error('WordPress endpoint not found. Please ensure:\n1. CPNode REST API Extension plugin is installed and activated\n2. WordPress permalink structure is enabled (Settings > Permalinks)\n3. The endpoint URL is correct: ' + url);
      }

      if (response.status === 401) {
        throw new Error('WordPress authentication failed. Please check:\n1. CPNode REST API Extension plugin is installed and activated\n2. WordPress username is correct\n3. Application Password is valid (should be 24 characters)\n4. User has "edit_posts" capability');
      }

      throw new Error(`Failed to fetch posts from WordPress: ${response.status} - ${errorText.substring(0, 200)}`);
    }

    // Check if we got HTML instead of JSON
    if (contentType && !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('Unexpected content type:', contentType, 'Response:', text.substring(0, 500));
      throw new Error(`WordPress returned HTML instead of JSON. This usually means:\n1. The plugin endpoint doesn't exist (plugin not activated)\n2. WordPress is misconfigured\n3. URL is incorrect: ${url}`);
    }

    return response.json();
  },

  async getPost(slug: string): Promise<WordPressPost> {
    const config = await getWordPressSiteConfig();
    if (!config?.wordpress_url) {
      throw new Error('WordPress URL not configured');
    }

    const response = await fetch(
      `${config.wordpress_url}/wp-json/wp/v2/posts?slug=${slug}&_embed=true`,
      {
        headers: getAuthHeaders(config.username, config.app_password),
      }
    );
    if (!response.ok) {
      throw new Error('Failed to fetch post');
    }
    const posts = await response.json();
    return posts[0];
  },

  async getAuthor(authorId: number): Promise<WordPressAuthor> {
    const config = await getWordPressSiteConfig();
    if (!config?.wordpress_url) {
      throw new Error('WordPress URL not configured');
    }

    const response = await fetch(
      `${config.wordpress_url}/wp-json/wp/v2/users/${authorId}`,
      {
        headers: getAuthHeaders(config.username, config.app_password),
      }
    );
    if (!response.ok) {
      throw new Error('Failed to fetch author');
    }
    return response.json();
  },

  async getPages(): Promise<Page[]> {
    const config = await getWordPressSiteConfig();
    if (!config?.wordpress_url) {
      throw new Error('WordPress URL not configured');
    }

    const response = await fetch(`${config.wordpress_url}/wp-json/wp/v2/pages`, {
      headers: getAuthHeaders(config.username, config.app_password),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch pages');
    }
    return response.json();
  },

  async getPage(slug: string): Promise<Page> {
    const config = await getWordPressSiteConfig();
    if (!config?.wordpress_url) {
      throw new Error('WordPress URL not configured');
    }

    const response = await fetch(
      `${config.wordpress_url}/wp-json/wp/v2/pages?slug=${slug}`,
      {
        headers: getAuthHeaders(config.username, config.app_password),
      }
    );
    if (!response.ok) {
      throw new Error('Failed to fetch page');
    }
    const pages = await response.json();
    return pages[0];
  },

  // Sync WordPress posts to Supabase database
  async syncPostsToDatabase(specificSiteLabel?: string): Promise<{ success: boolean; postsSynced: number; cardsCreated?: number; error?: string }> {
    try {
      console.log('Starting WordPress sync...');

      // Get the label ID for the configured site label
      // Use specificSiteLabel if provided, otherwise use the currently selected one
      const wpSiteLabel = specificSiteLabel || getWordPressSiteLabel();
      let siteLabel: { id: string; name: string; color: string } | null = null;
      let allSiteLabels: string[] = [];

      // Get all site labels so we can remove them from cards before adding the correct one
      const { data: allLabels } = await supabase
        .from('labels')
        .select('id, name');

      if (allLabels) {
        // Assume labels like "Site 1", "Site 2", etc. are site labels
        allSiteLabels = allLabels
          .filter(label => label.name.toLowerCase().startsWith('site '))
          .map(label => label.id);
      }

      if (wpSiteLabel) {
        console.log(`Looking up label: "${wpSiteLabel}"`);
        const { data: labelData } = await supabase
          .from('labels')
          .select('*')
          .eq('name', wpSiteLabel)
          .maybeSingle();

        if (labelData) {
          siteLabel = labelData;
          console.log(`Found label:`, siteLabel);
        } else {
          console.warn(`Label "${wpSiteLabel}" not found in database. Cards will not be tagged.`);
        }
      }

      // Create sync log entry
      console.log('Creating sync log entry...');
      const { data: syncLog, error: syncLogError } = await supabase
        .from('wordpress_sync_log')
        .insert({
          status: 'in_progress',
          posts_synced: 0,
        })
        .select()
        .single();

      if (syncLogError) {
        console.error('Sync log error:', syncLogError);
        throw syncLogError;
      }
      console.log('Sync log created:', syncLog);

      // Fetch all posts from WordPress
      console.log('Fetching posts from WordPress...');
      const posts = await this.getPosts(1, 100, wpSiteLabel);
      console.log(`Fetched ${posts.length} posts from WordPress`);
      let syncedCount = 0;
      let cardsCreated = 0;

      // Process each post
      for (const post of posts) {
        try {
          // Extract author info from embedded data
          const authorName = post._embedded?.author?.[0]?.name || 'Unknown';
          const authorId = post.author;

          // Get or create author
          const { data: existingAuthor } = await supabase
            .from('wordpress_authors')
            .select('*')
            .eq('wp_id', authorId)
            .maybeSingle();

          if (!existingAuthor && post._embedded?.author?.[0]) {
            const wpAuthor = post._embedded.author[0];
            await supabase.from('wordpress_authors').upsert({
              wp_id: wpAuthor.id,
              name: wpAuthor.name,
              slug: wpAuthor.slug,
              description: wpAuthor.description,
              avatar_url: wpAuthor.avatar_urls?.['96'] || null,
            });
          }

          // Upsert post
          const { error: postError } = await supabase
            .from('wordpress_posts')
            .upsert({
              wp_id: post.id,
              title: post.title.rendered,
              slug: post.slug,
              status: post.status,
              author_name: authorName,
              author_id: authorId,
              content: post.content.rendered,
              excerpt: post.excerpt.rendered,
              featured_media_url: post._embedded?.['wp:featuredmedia']?.[0]?.source_url || null,
              published_date: post.date,
              modified_date: post.modified,
              synced_at: new Date().toISOString(),
            }, {
              onConflict: 'wp_id',
            });

          if (!postError) {
            syncedCount++;

            // Create or update card for this WordPress post
            // Map WordPress status to board column
            let columnId = 'column-1'; // Default to Draft
            if (post.status === 'publish') {
              columnId = 'column-3'; // Published
            } else if (post.status === 'pending' || post.status === 'future') {
              columnId = 'column-1'; // Draft
            } else if (post.status === 'draft') {
              columnId = 'column-1'; // Draft
            }

            // Check if card already exists for this WordPress post from THIS site
            // We need to check both wordpress_post_id AND site label because different
            // WordPress sites can have posts with the same ID
            let existingCard = null;
            if (siteLabel) {
              const { data: cardsWithPostId } = await supabase
                .from('cards')
                .select('id, title, local_title, wordpress_post_id')
                .eq('wordpress_post_id', post.id);

              if (cardsWithPostId && cardsWithPostId.length > 0) {
                // Check which of these cards has the current site's label
                const currentSiteLabelId = siteLabel.id;
                for (const card of cardsWithPostId) {
                  const { data: cardLabels } = await supabase
                    .from('card_labels')
                    .select('label_id')
                    .eq('card_id', card.id);

                  if (cardLabels?.some(cl => cl.label_id === currentSiteLabelId)) {
                    existingCard = card;
                    break;
                  }
                }
              }
            }

            let cardId = existingCard?.id;

            if (existingCard) {
              // Update existing card (preserve local_title, owner, and due_date)
              await supabase
                .from('cards')
                .update({
                  title: post.title.rendered, // Update WordPress title
                  description: post.excerpt.rendered?.replace(/<[^>]*>/g, '').substring(0, 200), // Strip HTML
                  column_id: columnId,
                  // Keep existing local_title, owner, and due_date - don't overwrite user's data
                })
                .eq('id', existingCard.id);
            } else {
              // Create new card (owner and due_date left null for user to set)
              const { data: newCard, error: cardError } = await supabase
                .from('cards')
                .insert({
                  title: post.title.rendered,
                  description: post.excerpt.rendered?.replace(/<[^>]*>/g, '').substring(0, 200), // Strip HTML
                  column_id: columnId,
                  wordpress_post_id: post.id, // Link to WordPress post
                  // owner and due_date are null - user can set these independently
                })
                .select()
                .single();

              if (newCard && !cardError) {
                cardId = newCard.id;
                cardsCreated++;
              }
            }

            // Associate card with site label if configured
            if (cardId && siteLabel) {
              // First, remove ALL site labels from this card to prevent duplicate site assignments
              if (allSiteLabels.length > 0) {
                await supabase
                  .from('card_labels')
                  .delete()
                  .eq('card_id', cardId)
                  .in('label_id', allSiteLabels);
              }

              // Then add only the current site's label
              await supabase.from('card_labels').insert({
                card_id: cardId,
                label_id: siteLabel.id,
              });
            }
          }
        } catch (postErr) {
          console.error(`Failed to sync post ${post.id}:`, postErr);
        }
      }

      // Update sync log
      await supabase
        .from('wordpress_sync_log')
        .update({
          sync_completed_at: new Date().toISOString(),
          posts_synced: syncedCount,
          status: 'completed',
        })
        .eq('id', syncLog.id);

      console.log(`Sync complete: ${syncedCount} posts synced, ${cardsCreated} cards created`);
      return { success: true, postsSynced: syncedCount, cardsCreated };
    } catch (error) {
      console.error('WordPress sync failed - Full error:', error);

      let errorMessage = 'Unknown error';
      if (error instanceof Error) {
        errorMessage = error.message;

        // Provide helpful hints for common errors
        if (errorMessage.includes('relation') && errorMessage.includes('does not exist')) {
          errorMessage = 'Database tables not found. Please run WORDPRESS_TABLES.sql in Supabase SQL Editor first.';
        } else if (errorMessage.includes('Failed to fetch')) {
          errorMessage = 'Cannot connect to WordPress. Check your WordPress URL and credentials.';
        } else if (errorMessage.includes('401') || errorMessage.includes('authentication failed')) {
          errorMessage = 'WordPress authentication failed. Please ensure:\n1. CPNode REST API Extension plugin is installed and activated\n2. Application Password is complete (24 characters)\n3. Username and password are correct\n4. User has "edit_posts" capability';
        }
      }

      return {
        success: false,
        postsSynced: 0,
        cardsCreated: 0,
        error: errorMessage,
      };
    }
  },

  // Get WordPress posts from Supabase (already synced)
  async getSyncedPosts() {
    const { data } = await supabase
      .from('wordpress_posts')
      .select('*')
      .order('synced_at', { ascending: false });

    return data;
  },

  // Create cards from WordPress posts
  async createCardsFromWordPressPosts(labelId?: string) {
    const posts = await this.getSyncedPosts();
    if (!posts) return [];

    const cardsCreated = [];

    for (const post of posts) {
      // Map WordPress status to board column
      let columnId = 'column-1'; // Default to Draft
      if (post.status === 'publish') {
        columnId = 'column-3'; // Published
      } else if (post.status === 'pending' || post.status === 'future') {
        columnId = 'column-1'; // Draft
      } else if (post.status === 'draft') {
        columnId = 'column-1'; // Draft
      }

      // Create card (owner and due_date left null for user to set)
      const { data: card, error: cardError } = await supabase
        .from('cards')
        .insert({
          title: post.title,
          description: post.excerpt?.replace(/<[^>]*>/g, '').substring(0, 200), // Strip HTML and limit length
          column_id: columnId,
          // owner and due_date are null - user can set these independently
        })
        .select()
        .single();

      if (cardError) {
        console.error('Failed to create card:', cardError);
        continue;
      }

      if (card && labelId) {
        // Link card to label if provided
        const { error: labelError } = await supabase.from('card_labels').insert({
          card_id: card.id,
          label_id: labelId,
        });

        if (labelError) {
          console.error('Failed to link card to label:', labelError);
        }
      }

      if (card) {
        cardsCreated.push(card);
      }
    }

    return cardsCreated;
  },

  // Test WordPress connection and plugin availability
  async testConnection(): Promise<{ success: boolean; message: string; details?: any }> {
    try {
      const config = await getWordPressSiteConfig();

      if (!config) {
        return {
          success: false,
          message: 'WordPress site not configured. Please configure a site in the WordPress Site Configuration modal.',
        };
      }

      if (!config.wordpress_url) {
        return { success: false, message: 'WordPress URL not configured' };
      }

      if (!config.username || !config.app_password) {
        return {
          success: false,
          message: 'WordPress credentials not configured',
          details: {
            hasUsername: !!config.username,
            hasPassword: !!config.app_password,
            passwordLength: config.app_password?.length || 0,
          },
        };
      }

      // Test the custom endpoint
      const url = `${config.wordpress_url}/wp-json/cpnode/v1/posts?per_page=1`;
      console.log('Testing WordPress connection:', url);

      const response = await fetch(url, {
        headers: {
          ...getAuthHeaders(config.username, config.app_password),
          'Content-Type': 'application/json',
        },
        credentials: 'omit',
      });

      const contentType = response.headers.get('content-type');
      console.log('Test response:', { status: response.status, contentType });

      if (response.ok) {
        // Check if we got JSON
        if (contentType && !contentType.includes('application/json')) {
          const text = await response.text();
          console.error('Got HTML instead of JSON:', text.substring(0, 300));
          return {
            success: false,
            message: 'WordPress returned HTML instead of JSON. The plugin endpoint may not be registered. Please ensure:\n1. CPNode REST API Extension plugin is activated\n2. WordPress permalink structure is enabled',
            details: { status: response.status, contentType, preview: text.substring(0, 100) },
          };
        }

        const posts = await response.json();
        return {
          success: true,
          message: 'Connection successful! Plugin is working.',
          details: {
            postsFound: Array.isArray(posts) ? posts.length : 0,
            endpoint: url,
          },
        };
      } else if (response.status === 404) {
        const text = await response.text();
        console.error('404 response:', text.substring(0, 300));
        return {
          success: false,
          message: 'Endpoint not found (404). Please:\n1. Install and activate CPNode REST API Extension plugin\n2. Verify WordPress permalinks are enabled (Settings > Permalinks)\n3. Try deactivating and reactivating the plugin',
          details: { status: response.status, url },
        };
      } else if (response.status === 401) {
        return {
          success: false,
          message: 'Authentication failed (401). Check:\n1. Application Password is correct\n2. Username is correct\n3. User has "edit_posts" capability',
          details: {
            status: response.status,
            username: config.username,
            passwordLength: config.app_password.length,
          },
        };
      } else {
        const errorText = await response.text();
        console.error('Unexpected error:', { status: response.status, errorText: errorText.substring(0, 300) });
        return {
          success: false,
          message: `Connection failed: ${response.status}`,
          details: { status: response.status, error: errorText.substring(0, 200) },
        };
      }
    } catch (error) {
      console.error('Test connection error:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },
};
